import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Plus, Users, Lock, Crown } from 'lucide-react';
import { toast } from 'sonner';

export default function CommunitiesPage() {
  const { user } = useAuth();
  const [communities, setCommunities] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: '', description: '', is_private: false, is_paid: true, price: 9.95 });

  useEffect(() => { loadCommunities(); }, []);

  const loadCommunities = async () => {
    try {
      const res = await api.get('/communities');
      setCommunities(res.data);
    } catch { toast.error('Failed to load communities'); }
  };

  const handleCreate = async () => {
    if (!form.name.trim()) return;
    try {
      await api.post('/communities', { ...form, price: form.is_paid ? parseFloat(form.price) : 0 });
      setOpen(false);
      setForm({ name: '', description: '', is_private: false, is_paid: true, price: 9.95 });
      loadCommunities();
      toast.success('Community created');
    } catch { toast.error('Failed to create community'); }
  };

  const handleJoin = async (communityId, isPaid, price) => {
    if (isPaid && price > 0) {
      try {
        const res = await api.post('/payments/checkout', {
          community_id: communityId,
          origin_url: window.location.origin
        });
        window.location.href = res.data.url;
      } catch { toast.error('Payment setup failed'); }
      return;
    }
    try {
      await api.post(`/communities/${communityId}/join`);
      loadCommunities();
      toast.success('Joined community');
    } catch { toast.error('Failed to join'); }
  };

  return (
    <div className="animate-in" data-testid="communities-page">
      {/* Inner Circle Promo Banner */}
      <Link to="/inner-circle" className="block mb-6">
        <div className="bg-gradient-to-r from-gold/20 via-gold/10 to-gold/20 border border-gold/30 rounded-sm p-4 flex items-center justify-between hover:border-gold/50 transition-colors" data-testid="inner-circle-promo">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-gold/20 flex items-center justify-center">
              <Crown size={20} className="text-gold" />
            </div>
            <div>
              <h3 className="font-serif text-sm font-medium">Susan David Inner Circle</h3>
              <p className="text-xs text-muted-foreground">Exclusive private membership room • €5/month</p>
            </div>
          </div>
          <Button className="rounded-sm text-xs bg-gold text-obsidian hover:bg-obsidian hover:text-gold h-8 px-4">
            Join Inner Circle
          </Button>
        </div>
      </Link>

      <div className="flex items-center justify-between mb-8">
        <h1 className="font-serif text-2xl md:text-3xl">Communities</h1>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="create-community-btn" className="rounded-sm text-xs uppercase tracking-wider h-9 px-6 bg-obsidian hover:bg-gold hover:text-obsidian transition-colors duration-300">
              <Plus size={14} className="mr-1" /> Create
            </Button>
          </DialogTrigger>
          <DialogContent className="rounded-sm border-border/40">
            <DialogHeader>
              <DialogTitle className="font-serif text-xl">Create Community</DialogTitle>
              <DialogDescription className="text-xs text-muted-foreground">Set up a new community for your members.</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div>
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Name</Label>
                <Input data-testid="community-name-input" value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} className="rounded-none border-0 border-b border-input bg-transparent px-0 py-2 shadow-none focus-visible:ring-0" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Description</Label>
                <Textarea data-testid="community-desc-input" value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} className="rounded-none border-0 border-b border-input bg-transparent px-0 py-2 shadow-none focus-visible:ring-0 resize-none" />
              </div>
              <div className="flex items-center justify-between">
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Private Community</Label>
                <Switch data-testid="community-private-toggle" checked={form.is_private} onCheckedChange={v => setForm(p => ({ ...p, is_private: v }))} />
              </div>
              <div className="flex items-center justify-between">
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Paid Membership</Label>
                <Switch data-testid="community-paid-toggle" checked={form.is_paid} onCheckedChange={v => setForm(p => ({ ...p, is_paid: v }))} />
              </div>
              {form.is_paid && (
                <div>
                  <Label className="text-xs uppercase tracking-wider text-muted-foreground">Monthly Price (EUR)</Label>
                  <Input data-testid="community-price-input" type="number" step="0.01" value={form.price} onChange={e => setForm(p => ({ ...p, price: e.target.value }))} className="rounded-none border-0 border-b border-input bg-transparent px-0 py-2 shadow-none focus-visible:ring-0" />
                </div>
              )}
              <Button data-testid="community-create-submit" onClick={handleCreate} className="w-full rounded-sm h-12 uppercase tracking-wider text-xs bg-obsidian hover:bg-gold hover:text-obsidian transition-colors duration-300">
                Create Community
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {communities.length === 0 && (
          <p className="col-span-full text-center text-muted-foreground text-sm py-12">No communities yet. Create the first one.</p>
        )}
        {communities.map((c, i) => {
          const isMember = c.members?.includes(user?.id);
          return (
            <div key={c.id} className="border border-border/40 hover:border-gold/50 transition-colors duration-500 animate-in group" style={{ animationDelay: `${i * 80}ms` }} data-testid={`community-card-${c.id}`}>
              <div className="aspect-[3/2] bg-mist relative overflow-hidden">
                {c.cover_image ? (
                  <img src={`data:image/jpeg;base64,${c.cover_image}`} alt={c.name} className="w-full h-full object-cover img-art" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center bg-obsidian/5">
                    <Users size={32} className="text-muted-foreground/30" />
                  </div>
                )}
                <div className="absolute top-3 right-3 flex gap-1.5">
                  {c.is_private && <Badge className="bg-obsidian text-white rounded-sm text-[10px] uppercase"><Lock size={8} className="mr-1" /> Private</Badge>}
                  {c.is_paid && <Badge className="bg-gold text-obsidian rounded-sm text-[10px] uppercase"><Crown size={8} className="mr-1" /> {c.price > 0 ? `${c.price.toFixed(2)}/mo` : 'Paid'}</Badge>}
                </div>
              </div>
              <div className="p-4">
                <h3 className="font-serif text-base mb-1">{c.name}</h3>
                <p className="text-xs text-muted-foreground line-clamp-2 mb-3">{c.description}</p>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-muted-foreground">{c.members?.length || 0} members</span>
                  {isMember ? (
                    <Link to={`/communities/${c.id}`}>
                      <Button data-testid={`enter-community-${c.id}`} variant="outline" size="sm" className="rounded-sm text-xs uppercase tracking-wider">
                        Enter
                      </Button>
                    </Link>
                  ) : (
                    <Button data-testid={`join-community-${c.id}`} size="sm" onClick={() => handleJoin(c.id, c.is_paid, c.price)} className="rounded-sm text-xs uppercase tracking-wider bg-obsidian hover:bg-gold hover:text-obsidian transition-colors duration-300">
                      {c.is_paid && c.price > 0 ? `Join - ${c.price.toFixed(2)}/mo` : 'Join'}
                    </Button>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
